import { useEffect, useState, type FormEvent } from 'react';
import { trpc, type IdentityTrpcClient } from '../../lib/trpc';
import { navigate } from '../../lib/router';
import { Icon } from '../../components/icons';
import { Logo } from '../../components/Logo';
import { Button, Card, Field, Input, Notice } from '../../components/ui';
import { RealmFieldInput } from '../../components/RealmFieldInput';
import { LanguageSelector } from '../../components/LanguageSelector';
import { localizedError, useI18n, useInteractionLocale } from '../../lib/i18n';
import { useGoogleOAuthAvailable } from '../../lib/google-federation';

interface Props { realmSlug: string; uid: string; client?: IdentityTrpcClient; onAuthorizationUrl?: (url: string) => void }
type Details = Awaited<ReturnType<IdentityTrpcClient['interaction']['details']['query']>>;
type OnboardingState = { requiredFieldKeys: string[]; prefilledFieldKeys: string[]; fields: Array<{ key: string; label: string; required: boolean; schema: Record<string, unknown>; value?: unknown }> };
type Profile = { displayName: string; email: string };
type View = 'password' | 'recovery' | 'google-link' | 'onboarding' | 'access-denied';

function scopeLabels(t: ReturnType<typeof useI18n>['t']): Record<string, { name: string; detail: string }> {
  return {
    openid: { name: t('publicScopeOpenidName'), detail: t('publicScopeOpenidDetail') },
    profile: { name: t('publicScopeProfileName'), detail: t('publicScopeProfileDetail') },
    email: { name: t('publicScopeEmailName'), detail: t('publicScopeEmailDetail') },
    offline_access: { name: t('publicScopeOfflineAccessName'), detail: t('publicScopeOfflineAccessDetail') },
  };
}

function AuthFrame({ realmSlug, children }: { realmSlug: string; children: React.ReactNode }) {
  const { t } = useI18n();
  return <main className="auth-page">
    <div className="auth-brand"><Logo/><span className="realm-pill"><i/> {realmSlug}</span><LanguageSelector/></div>
    {children}
    <p className="auth-footer">{t('protectedBy')} · <a href="#privacy">{t('publicPrivacy')}</a></p>
  </main>;
}

function GoogleMark() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M21.6 12.2c0-.7-.1-1.5-.2-2.2H12v4.1h5.4a4.7 4.7 0 0 1-2 3v2.7h3.4c2-1.8 3.1-4.5 3.1-7.6Z"/><path fill="#34A853" d="M12 22c2.8 0 5.1-.9 6.8-2.5l-3.4-2.7c-.9.6-2.1 1-3.4 1a6 6 0 0 1-5.6-4.1H3v2.7A10.3 10.3 0 0 0 12 22Z"/><path fill="#FBBC05" d="M6.4 13.7a6 6 0 0 1 0-3.8V7.2H3a10.1 10.1 0 0 0 0 9.2l3.4-2.7Z"/><path fill="#EA4335" d="M12 5.8c1.5 0 2.9.5 4 1.6l3-3A10 10 0 0 0 3 7.2l3.4 2.7A6 6 0 0 1 12 5.8Z"/></svg>;
}

function isAccessDenied(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const result = error as { data?: { code?: unknown }; message?: unknown };
  return result.data?.code === 'FORBIDDEN' || result.message === 'Identity cannot access this realm';
}

export function InteractionPage({ realmSlug, uid, client = trpc, onAuthorizationUrl = (url) => window.location.assign(url) }: Props) {
  const { t } = useI18n();
  const googleOAuthAvailable = useGoogleOAuthAvailable(client);
  const [details, setDetails] = useState<Details>();
  useInteractionLocale(details?.uiLocales);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState<View>('password');
  const [onboarding, setOnboarding] = useState<OnboardingState>();
  const [profile, setProfile] = useState<Profile>();
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [linkTotp, setLinkTotp] = useState('');
  const [linkRecoveryCode, setLinkRecoveryCode] = useState('');
  const [linkWithRecovery, setLinkWithRecovery] = useState(false);

  useEffect(() => {
    let active = true;
    client.interaction.details.query({ realmSlug, uid }).then((result) => {
      if (active) setDetails(result);
    }).catch((caught) => {
      if (active) setError(localizedError(caught, t));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, realmSlug, uid]);

  useEffect(() => {
    let active = true;
    const resume = client.googleFederation?.resumeInteraction;
    if (!resume) return () => { active = false; };
    resume.mutate().then((result) => {
      if (!active || result.status !== 'onboarding' || result.realmSlug !== realmSlug || result.uid !== uid) return;
      beginOnboarding(result.onboarding);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [client, realmSlug, uid]);

  function beginOnboarding(state: OnboardingState, activeProfile?: Profile) {
    setOnboarding(state);
    setProfile(activeProfile);
    setFieldValues(Object.fromEntries(state.fields.map((field) => [field.key, field.value ?? null])));
    setView('onboarding');
  }

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    if (!details) return;
    setSubmitting(true); setError('');
    try {
      const input = { realmSlug, uid, email, password, ...(view === 'recovery' && recoveryCode ? { recoveryCode: recoveryCode.toUpperCase() } : {}), ...(view === 'password' && totp ? { totp } : {}) };
      const result = await client.interaction.login.mutate(input);
      if (result.status === 'complete') window.location.assign(result.redirectTo);
      else beginOnboarding(result.onboarding);
    } catch (caught) {
      if (isAccessDenied(caught)) setView('access-denied');
      else setError(localizedError(caught, t));
    } finally { setSubmitting(false); }
  }

  async function startGoogle() {
    setSubmitting(true); setError('');
    try {
      const result = await client.interaction.beginGoogle.mutate({ realmSlug, uid });
      onAuthorizationUrl(result.authorizationUrl);
    } catch (caught) {
      setError(localizedError(caught, t));
    } finally { setSubmitting(false); }
  }

  async function linkGoogle(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true); setError('');
    try {
      const credentials = { realmSlug, uid, email: linkEmail, password: linkPassword, ...(linkWithRecovery && linkRecoveryCode ? { recoveryCode: linkRecoveryCode.toUpperCase() } : {}), ...(!linkWithRecovery && linkTotp ? { totp: linkTotp } : {}) };
      if (!googleOAuthAvailable) { setError(t('errorLink')); return; }
      const result = await client.interaction.beginGoogleLink.mutate(credentials);
      onAuthorizationUrl(result.authorizationUrl);
    } catch (caught) {
      if (isAccessDenied(caught)) setView('access-denied');
      else setError(t('errorLink'));
    } finally { setSubmitting(false); }
  }

  async function finishOnboarding(fields: Record<string, unknown>) {
    setSubmitting(true); setError('');
    try {
      const result = await client.interaction.completeOnboarding.mutate({ realmSlug, uid, fields });
      window.location.assign(result.redirectTo);
    } catch (caught) {
      if (isAccessDenied(caught)) setView('access-denied');
      else setError(localizedError(caught, t));
      setSubmitting(false);
    }
  }

  if (loading) return <AuthFrame realmSlug={realmSlug}><Card className="auth-card loading-card"><span className="spinner"/><p>{t('interactionLoading')}</p></Card></AuthFrame>;
  if (!details) return <AuthFrame realmSlug={realmSlug}><Card className="auth-card"><Notice tone="danger" title={t('cannotContinue')}>{error}</Notice><Button variant="secondary" onClick={() => navigate('/')}>{t('navBackAccount')}</Button></Card></AuthFrame>;
  if (details.prompt === 'consent') return <ConsentView realmSlug={realmSlug} details={details} client={client}/>;

  if (view === 'access-denied') {
    return <AuthFrame realmSlug={realmSlug}><Card className="auth-card"><div className="auth-heading"><h1>{t('accessDenied')}</h1><p>{t('accessDeniedDescription')}</p></div></Card></AuthFrame>;
  }

  if (view === 'google-link') {
    return <AuthFrame realmSlug={realmSlug}><Card className="auth-card">
      <div className="auth-heading"><h1>{t('publicAlreadyHaveIdentityTitle')}</h1><p>{t('publicAlreadyHaveIdentityDescription')}</p></div>
      {error && <Notice tone="danger">{error}</Notice>}
      <form onSubmit={(event) => void linkGoogle(event)}>
        <Field label={t('email')} required><Input type="email" autoComplete="username" value={linkEmail} onChange={(event) => setLinkEmail(event.target.value)} required/></Field>
        <Field label={t('password')} required><Input type="password" autoComplete="current-password" value={linkPassword} onChange={(event) => setLinkPassword(event.target.value)} required/></Field>
        {linkWithRecovery ? <Field label={t('recoveryCode')} hint={t('publicRecoveryCodeFormat')}><Input autoComplete="one-time-code" value={linkRecoveryCode} onChange={(event) => setLinkRecoveryCode(event.target.value)} pattern="[A-Z2-9]{5}-[A-Z2-9]{5}"/></Field> : <Field label={t('verificationCode')} hint={t('publicVerificationCodeHint')}><Input inputMode="numeric" autoComplete="one-time-code" value={linkTotp} onChange={(event) => setLinkTotp(event.target.value)} pattern="[0-9]{6}" maxLength={6}/></Field>}
        <Button className="full-button" type="submit" disabled={submitting}>{submitting ? t('continue') : t('continue')}</Button>
      </form>
      <button type="button" className="text-button" onClick={() => setLinkWithRecovery((current) => !current)}>{linkWithRecovery ? t('useAuthenticator') : t('useRecovery')}</button>
      <button type="button" className="text-button secondary-text-button" onClick={() => { setError(''); setView('password'); }}>{t('back')}</button>
    </Card></AuthFrame>;
  }

  if (view === 'onboarding' && onboarding) {
    return <AuthFrame realmSlug={realmSlug}><Card className="auth-card auth-card-wide">
      <div className="auth-heading"><h1>{t('publicCompleteProfileTitle')}</h1><p>{t('publicCompleteProfileDescription', { realm: realmSlug })}</p></div>
      {error && <Notice tone="danger">{error}</Notice>}
      <form onSubmit={(event) => { event.preventDefault(); void finishOnboarding(fieldValues); }} className="form-grid">
        {profile && <Field label={t('email')}><Input value={profile.email} readOnly aria-readonly="true"/></Field>}
        {onboarding.fields.map((field) => <RealmFieldInput key={field.key} field={field} value={fieldValues[field.key]} disabled={submitting} onChange={(value) => setFieldValues((current) => ({ ...current, [field.key]: value }))}/>)}
        {onboarding.prefilledFieldKeys.length > 0 && <p className="prefill-note"><Icon name="check"/> {t('publicPrefilledFields')}</p>}
        <div className="form-actions full"><Button type="submit" disabled={submitting}>{submitting ? t('save') : t('continue')}</Button></div>
      </form>
    </Card></AuthFrame>;
  }

  return <AuthFrame realmSlug={realmSlug}><Card className="auth-card">
    <div className="auth-heading"><h1>{t('accountTitle')}</h1><p>{t('continueTo', { client: details.clientId || t('publicRequestingApplication'), realm: realmSlug })}</p></div>
    {error && <Notice tone="danger">{error}</Notice>}
    {googleOAuthAvailable && <Button className="google-button" variant="secondary" onClick={() => void startGoogle()} disabled={submitting}><GoogleMark/> {t('googleContinue')}</Button>}
    <button type="button" className="text-button google-link-button" onClick={() => { setError(''); setView('google-link'); }}>{t('alreadyHaveIdentity')}</button>
    <div className="divider"><span>{t('emailDivider')}</span></div>
    <form onSubmit={(event) => void submitPassword(event)}>
      <Field label={t('email')} required><Input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required/></Field>
      <Field label={t('password')} required><Input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required/></Field>
      {view === 'recovery' ? <Field label={t('recoveryCode')} hint={t('publicRecoveryCodeFormat')} required><Input autoComplete="one-time-code" value={recoveryCode} onChange={(event) => setRecoveryCode(event.target.value)} pattern="[A-Z2-9]{5}-[A-Z2-9]{5}" required/></Field> : <Field label={t('verificationCode')} hint={t('publicVerificationCodeHint')}><Input inputMode="numeric" autoComplete="one-time-code" value={totp} onChange={(event) => setTotp(event.target.value)} pattern="[0-9]{6}" maxLength={6}/></Field>}
      <Button className="full-button" type="submit" disabled={submitting}>{submitting ? t('signIn') : t('signIn')}</Button>
    </form>
    <button className="text-button" onClick={() => setView(view === 'recovery' ? 'password' : 'recovery')}>{view === 'recovery' ? t('useAuthenticator') : t('useRecovery')}</button>
  </Card></AuthFrame>;
}


function ConsentView({ realmSlug, details, client }: { realmSlug: string; details: Details; client: IdentityTrpcClient }) {
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  async function answer(accept: boolean) {
    setSubmitting(true); setError('');
    try {
      const result = await client.interaction.consent.mutate({ realmSlug, uid: details.uid, accept });
      window.location.assign(result.redirectTo);
    } catch { setError(t('errorGeneric')); setSubmitting(false); }
  }
  const labels = scopeLabels(t);
  const permissions = [...new Set([...details.scopes, ...details.claims])];
  return <AuthFrame realmSlug={realmSlug}><Card className="auth-card consent-card">
    <div className="client-symbol">{details.clientId.slice(0, 2).toUpperCase() || t('publicClientInitialsFallback')}</div>
    <div className="auth-heading"><h1>{t('consentRequest', { client: details.clientId || t('publicRequestingApplication') })}</h1><p>{t('consentDescription')}</p></div>
    {error && <Notice tone="danger">{error}</Notice>}
    <ul className="permission-list">{permissions.map((scope) => { const item = labels[scope] ?? { name: scope, detail: t('publicScopeFallbackDetail') }; return <li key={scope}><span><Icon name="check"/></span><div><strong>{item.name}</strong><small>{item.detail}</small></div></li>; })}</ul>
    <Notice>{t('passwordNotice')}</Notice>
    <div className="form-actions"><Button variant="ghost" disabled={submitting} onClick={() => void answer(false)}>{t('cancel')}</Button><Button disabled={submitting} onClick={() => void answer(true)}>{t('allow')}</Button></div>
  </Card></AuthFrame>;
}
